/**
 * Enrollment Service
 * Handles all enrollment-related API calls for Learner mode
 *
 * Uses shared API client with automatic token refresh for session extension
 */

import { AxiosError } from 'axios';
import { apiClient } from './apiClient';
import {
  Enrollment,
  EnrollmentStatus,
  EnrolledCourse,
  EnrollCourseDto,
} from '../types/enrollment';
import { ApiResponse } from '../types';

// Error handler helper
interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
  statusCode?: number;
}

const handleApiError = (error: AxiosError): ApiError => {
  if (error.response) {
    const data = error.response.data as {
      message?: string;
      error?: string | { code?: string; message?: string };
      details?: Record<string, string[]>;
    };

    let errorMessage = 'An error occurred';
    let errorCode = error.response.status.toString();

    if (data.message) {
      errorMessage = data.message;
    } else if (data.error) {
      if (typeof data.error === 'string') {
        errorMessage = data.error;
      } else if (typeof data.error === 'object' && data.error.message) {
        errorMessage = data.error.message;
        if (data.error.code) {
          errorCode = data.error.code;
        }
      }
    }

    return {
      code: errorCode,
      message: errorMessage,
      details: data.details,
      statusCode: error.response.status,
    };
  }

  if (error.request) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Unable to connect to server. Please check your internet connection.',
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: error.message || 'An unexpected error occurred',
  };
};

// ============================================
// ENROLLMENT API METHODS
// ============================================

/**
 * Enroll in a course
 */
export const enroll = async (
  courseId: string,
  dto?: EnrollCourseDto
): Promise<Enrollment> => {
  try {
    const response = await apiClient.post<ApiResponse<Enrollment>>(
      `/courses/${courseId}/enroll`,
      dto || {}
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Get all enrollments for the current user (with course data)
 */
export const getMyEnrollments = async (): Promise<EnrolledCourse[]> => {
  try {
    const response = await apiClient.get<ApiResponse<EnrolledCourse[]>>(
      '/enrollments/me'
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Check if the current user is enrolled in a specific course
 */
export const checkEnrollment = async (
  courseId: string
): Promise<EnrollmentStatus> => {
  try {
    const response = await apiClient.get<ApiResponse<EnrollmentStatus>>(
      `/courses/${courseId}/enrollment`
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Unenroll from a course
 */
export const unenroll = async (courseId: string): Promise<void> => {
  try {
    await apiClient.delete(`/courses/${courseId}/enroll`);
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

const enrollmentService = {
  enroll,
  unenroll,
  getMyEnrollments,
  checkEnrollment,
};

export default enrollmentService;
